import { createCustomFilterForEntity } from "@shared/backend/create-custom-filter-for-entity";
import {
  filterWithRelationsAndJson,
  type RelationalOrJsonEntityFilter,
} from "@shared/helpers/sql-relations";
import { Association } from "./";
import { dbNamesOf, remult, SqlDatabase } from "remult";
import { User } from "../user";

const createCustomFilter = createCustomFilterForEntity<Association>();

export class AssociationFilters {
  /**
   * # 🌟✨ HyperFilter – The Ultimate Filtering Powerhouse!
   * ### 🤝 Filter through complex relations effortlessly
   * ### 📦 Directly filter JSON fields (no backend work required!)
   * ### 🚀 Transforms your filter into a single, optimized DB query
   * ### 🔒 Engineered for maximum SQL injection protection
   *
   * ---
   * ### Example:
   * ```ts
   * myEntityRepo.find({
   *  where: MyEntity.hyperFilter({ ... })
   * })
   * ```
   *
   * ---
   * Built by Natan Salmon
   */
  static hyperFilter = createCustomFilter<
    RelationalOrJsonEntityFilter<Association>
  >((filters) => {
    return filterWithRelationsAndJson(Association)(filters);
  });

  static get dbNames() {
    return dbNamesOf(Association, {
      tableName: true,
    });
  }

  static isAddressInRadiusFromPoint = createCustomFilter<{
    lat: number;
    lng: number;
    radius: {
      distance: number;
      measure: "km" | "m";
    };
  }>((coordinates) => {
    return SqlDatabase.rawFilter(async (build) => {
      const {
        lat,
        lng,
        radius: { distance, measure },
      } = coordinates;

      const dbNames = await this.dbNames;
      const associationTable = dbNames;
      const radiusMeters = measure === "km" ? distance * 1000 : distance;

      build.sql = `
      EXISTS (
        SELECT 1
        FROM "user" u
        JOIN "association" a ON a."user_id" = u.id
        WHERE a.id = ${associationTable}.id AND (
          (${build.param(lat)}::float = 0 AND ${build.param(lng)}::float = 0)
          OR ST_DWithin(
            u.coordinates_geog,
            ST_SetSRID(ST_MakePoint(${build.param(lng)}, ${build.param(lat)}), 4326)::geography,
            ${build.param(radiusMeters)}
          )
        )
      )
    `;
    });
  });
}
